import type {LowLatencyAPI, PlayerAPI, PlayerConfig, TimeMode} from 'bitmovin-player';
import type {PlayerAdvertisingAPI} from 'bitmovin-player/types/advertising/API';
import type {PlaybackConfig} from 'bitmovin-player/types/core/PlayerConfigAPI';
import type {PlayerSubtitlesAPI} from 'bitmovin-player/types/subtitles/API';

import type {MaybeReturn} from '../../types/MaybeReturn';
import {safeAccess} from '../../utils/objectUtils';

export class Bitmovin8PlayerProxy {
  constructor(private readonly rawPlayer: PlayerAPI) {}

  on: PlayerAPI['on'] = (eventType, callback) => {
    safeAccess(() => this.rawPlayer.on(eventType, callback));
  };

  getCurrentTime: MaybeReturn<PlayerAPI['getCurrentTime']> = (mode?: TimeMode) =>
    safeAccess(() => this.rawPlayer.getCurrentTime(mode));

  getDuration: MaybeReturn<PlayerAPI['getDuration']> = () => safeAccess(() => this.rawPlayer.getDuration());

  get version(): PlayerAPI['version'] | undefined {
    return safeAccess(() => this.rawPlayer.version);
  }

  getPlayerType: MaybeReturn<PlayerAPI['getPlayerType']> = () => safeAccess(() => this.rawPlayer.getPlayerType());

  getViewMode: MaybeReturn<PlayerAPI['getViewMode']> = () => safeAccess(() => this.rawPlayer.getViewMode());

  getContainer: MaybeReturn<PlayerAPI['getContainer']> = () => safeAccess(() => this.rawPlayer.getContainer());

  isLive: MaybeReturn<PlayerAPI['isLive']> = () => safeAccess(() => this.rawPlayer.isLive());

  /**
   * Reports `null`, `Infinity` or `NaN` when there is no latency to report, for example for a VOD
   * source, despite the player typing it as `number`.
   *
   * @see https://cdn.bitmovin.com/player/web/8/docs/interfaces/Core.LowLatencyAPI.html
   */
  getLatency: MaybeReturn<LowLatencyAPI['getLatency']> = () =>
    safeAccess(() => this.rawPlayer.lowlatency?.getLatency());

  /**
   * Reports `-1` while no source is loaded and `0` for a source that is not configured for low
   * latency.
   *
   * @see https://cdn.bitmovin.com/player/web/8/docs/interfaces/Core.LowLatencyAPI.html
   */
  getTargetLatency: MaybeReturn<LowLatencyAPI['getTargetLatency']> = () =>
    safeAccess(() => this.rawPlayer.lowlatency?.getTargetLatency());

  /**
   * Reports the offset to the live edge in seconds, negative behind it and `0` at it as well as for
   * a source that cannot time shift.
   */
  getTimeShift: MaybeReturn<PlayerAPI['getTimeShift']> = () => safeAccess(() => this.rawPlayer.getTimeShift());

  getStreamType: MaybeReturn<PlayerAPI['getStreamType']> = () => safeAccess(() => this.rawPlayer.getStreamType());

  isMuted: MaybeReturn<PlayerAPI['isMuted']> = () => safeAccess(() => this.rawPlayer.isMuted());

  isCasting: MaybeReturn<PlayerAPI['isCasting']> = () => safeAccess(() => this.rawPlayer.isCasting());

  isAirplayActive: MaybeReturn<PlayerAPI['isAirplayActive']> = () => safeAccess(() => this.rawPlayer.isAirplayActive());

  getAudio: MaybeReturn<PlayerAPI['getAudio']> = () => safeAccess(() => this.rawPlayer.getAudio());

  getSource: MaybeReturn<PlayerAPI['getSource']> = () => safeAccess(() => this.rawPlayer.getSource());

  getDroppedVideoFrames: MaybeReturn<PlayerAPI['getDroppedVideoFrames']> = () =>
    safeAccess(() => this.rawPlayer.getDroppedVideoFrames());

  getPlaybackVideoData: MaybeReturn<PlayerAPI['getPlaybackVideoData']> = () =>
    safeAccess(() => this.rawPlayer.getPlaybackVideoData());

  getPlaybackAudioData: MaybeReturn<PlayerAPI['getPlaybackAudioData']> = () =>
    safeAccess(() => this.rawPlayer.getPlaybackAudioData());

  getConfig: MaybeReturn<PlayerAPI['getConfig']> = () => safeAccess(() => this.rawPlayer.getConfig());

  get remoteControlConfig(): PlayerConfig['remotecontrol'] | undefined {
    return this.getConfig()?.remotecontrol;
  }

  getSubtitleList: MaybeReturn<PlayerSubtitlesAPI['list']> = () => safeAccess(() => this.rawPlayer.subtitles?.list());

  isLinearAdActive: MaybeReturn<PlayerAdvertisingAPI['isLinearAdActive']> = () =>
    safeAccess(() => this.rawPlayer.ads?.isLinearAdActive());

  getActiveAd: MaybeReturn<PlayerAdvertisingAPI['getActiveAd']> = () =>
    safeAccess(() => this.rawPlayer.ads?.getActiveAd());

  getAdModuleInfo: MaybeReturn<PlayerAdvertisingAPI['getModuleInfo']> = () =>
    safeAccess(() => this.rawPlayer.ads?.getModuleInfo());

  get autoplay(): PlaybackConfig['autoplay'] | undefined {
    return this.getConfig()?.playback?.autoplay;
  }
}
